<template>
  <div
    class="incoming-call-frame"
    :class="[showIncomingCall ? 'display-incoming-call' : '', positionClass]"
    :style="'min-height:' + this.attrHeight + 'px;'"
  >
    <!-- Sidebar -->
    <div class="call-center-toggle-menu">
      Incoming Call
      <font-awesome-icon
        @click="closeIncomingCall()"
        icon="times-circle"
        class="call-toggle-button pull-right"
      />
    </div>
    <!-- Load in each component -->
    <active-call-name
      :caller-name="callerName"
      :phone-number="phoneNumber"
    ></active-call-name>
    <div class="queueName" v-if="queueName">
      {{ queueName }}
    </div>
    <div class="queueName" v-if="phoneLabel">
      {{ phoneLabel }}
    </div>
    <div v-if="hasHeroTags" class="incoming-call-hero-tags">
      <hero-tags :tags="contactTags" readonly></hero-tags>
    </div>
    <div v-if="attributesLength > 0" class="incoming-call-attributes">
      <el-row v-for="row in stayAttributes" :key="row.label">
        <el-col :span="8"
          ><small>{{ row.label }}</small></el-col
        >
        <el-col :span="16" align="center"
          ><small>{{ row.value }}</small></el-col
        >
      </el-row>
    </div>
    <el-row style="margin-top: 10px">
      <el-col :span="12" align="center">
        <el-button type="danger" @click="$emit('reject', callId)" circle
          ><font-awesome-icon icon="phone" /></el-button
        ><br />Decline
      </el-col>
      <el-col :span="12" align="center" v-show="allowAnswer">
        <el-button type="primary" @click="answerCall()" circle
          ><font-awesome-icon icon="phone" /></el-button
        ><br />Accept
      </el-col>
    </el-row>
  </div>
</template>
<script>
import { markRaw } from 'vue';
import { mapGetters } from 'vuex';

import ActiveCallName from './ActiveCallName';
import HeroTags from 'src/components/UIComponents/HeroTags.vue';
import { MultiSinkAudio } from 'src/util/audioDevices';
import { useAudioDevices } from 'src/composables/useAudioDevices';

const RING_URL =
  'https://adbase-static.s3.amazonaws.com/sounds/telephone_digital_ring.mp3';

export default {
  components: {
    ActiveCallName,
    HeroTags,
  },
  setup() {
    const { ringSinkIds } = useAudioDevices();
    return { ringSinkIds };
  },
  computed: {
    ...mapGetters('appConfig', ['getAppConfig']),
    ...mapGetters('queues', ['getQueues']),
    ...mapGetters('phoneNumbers', ['getPhoneNumbers']),
    ...mapGetters('realtime', ['getCallData', 'getCallById']),
    ...mapGetters('tags', ['getTags']),
    hasHeroTags() {
      const ids = new Set(this.contactTags.map((t) => t.id));
      return !!this.getTags?.some((t) => t.is_hero != null && ids.has(t.id));
    },
    attributesLength() {
      return Object.keys(this.stayAttributes).length;
    },
    positionClass() {
      // Use prop if provided, otherwise check app config, default to bottom-right
      const pos = this.position || this.getAppConfig?.callCenter?.incomingCallPosition || 'bottom-right';
      return `incoming-call-${pos}`;
    },
  },
  created() {
    // Set these immediately but show the popup right away
    // Then as more data comes in populate the rest
    // Only need to do this for Band
    if (this.playRing) {
      const ringUrl = this.getAppConfig?.callCenter?.ringAudio ?? RING_URL;
      this.ringAudio = markRaw(
        new MultiSinkAudio(ringUrl, this.ringSinkIds)
      );
      // Play ring right away too
      this.ringAudio.play();
      this.ringInterval = setInterval(() => {
        this.ringAudio.play();
      }, 2000);
    }
    // Only bandwidth as they dont have callbacks
    if (this.autoReject) {
      this.rejectTimeout = setTimeout(() => {
        if (this.getCallById(this.callId).call_status != 'answered') {
          this.$emit('reject', this.callId);
        }
      }, this.rejectDelay);
    }
  },
  beforeUnmount() {
    if (this.ringAudio) {
      this.ringAudio.destroy();
      this.ringAudio = undefined;
    }
    if (this.ringInterval) {
      clearInterval(this.ringInterval);
      this.ringInterval = undefined;
    }
    if (this.rejectTimeout) {
      clearTimeout(this.rejectTimeout);
      this.rejectTimeout = undefined;
    }
  },
  props: {
    callData: Object, // Updated data, eventually
    allowAnswer: Boolean,
    playRing: Boolean,
    callId: {
      type: Number, // Call object id
      required: true,
    },
    // call: Object,
    autoReject: {
      type: Boolean,
      required: true,
    },
    queueId: Number,
    position: {
      type: String,
      default: 'bottom-right',
      validator: (value) => ['bottom-left', 'bottom-center', 'bottom-right'].includes(value),
    },
  },
  watch: {
    // Follow changes from the audio settings while ringing
    ringSinkIds(ids) {
      this.ringAudio?.setSinks(ids);
    },
    callData: {
      immediate: true,
      handler(callData) {
        try {
          this.buildIncomingCallData(this.callData);
        } catch (e) {
          // sometimes we can't always build the data fast enough.
          // - this will fault the front end, and
          //   place the backend in wrapup once the call ends.
          console.error("Can't render incoming data:", e);
        }
      },
    },
    // Caller tags come from the cached contact, fetched once if not cached
    'callData.contact_id': {
      immediate: true,
      async handler(contactId) {
        if (!contactId) {
          return;
        }
        try {
          const contact = await this.$store.dispatch(
            'contacts/fetchContact',
            contactId
          );
          if (this.callData?.contact_id == contactId) {
            this.contactTags = contact?.tags || [];
          }
        } catch (e) {
          // Drop the failed fetch from the cache so it can be retried
          this.$store.dispatch('contacts/deleteContactCache', contactId);
          console.error('Unable to load caller tags:', e);
        }
      },
    },
    queueId: {
      immediate: true,
      handler(queueId) {
        if (!queueId) {
          return;
        }
        this.queueName = this.getQueues.find((q) => queueId == q.id)?.name;
      },
    },
  },
  data() {
    return {
      attrHeight: 180,
      callerName: '',
      queueName: undefined,
      phoneNumber: '',
      phoneLabel: undefined,
      contactTags: [], // caller tags for the hero tags
      ringAudio: undefined,
      ringInterval: undefined,
      rejectTimeout: undefined,
      rejectDelay: 15000, // miliseconds before auto reject
      showIncomingCall: true, // TODO: remove this
      stayAttributes: [
        // { key: 'total_revenue', label: 'Revenue', value: '' },
        // { key: 'arrival_date', label: 'Arrival', value: '' },
        // { key: 'departure_date', label: 'Departure', value: '' },
        // { key: 'unit_code', label: 'unit', value: '' },
      ],
    };
  },
  methods: {
    closeIncomingCall() {
      this.showIncomingCall = false;
    },
    answerCall() {
      if (this.rejectTimeout) {
        clearTimeout(this.rejectTimeout);
        this.rejectTimeout = undefined;
      }
      this.$emit('answer', this.callId);
      if (this.ringAudio) {
        this.ringAudio.pause();
      }
      if (this.ringInterval) {
        clearInterval(this.ringInterval);
        this.ringInterval = undefined;
      }
    },
    async buildIncomingCallData(data) {
      if (data == undefined) {
        return;
      }
      this.callerName = data.first_name + ' ' + data.last_name;
      this.phoneNumber = data.from_number;
      this.phoneLabel = this.getPhoneNumbers.find((n) => n.phone_number == data.from_number)?.label;

      // Populate the rest when it becomes available
      if (!data.reservation) {
        return;
      }

      const fieldTypes = {
        total_revenue: 'currency',
        arrival_date: 'date',
        departure_date: 'date',
        unit_code: 'string',
      };
      const fieldLabels = {
        total_revenue: 'Revenue',
        arrival_date: 'Arrival',
        departure_date: 'Departure',
        unit_code: 'Unit Code',
      };

      for (const [k, v] of Object.entries(fieldTypes)) {
        let value;
        if (data.reservation[k]) {
          switch (v) {
            case 'currency':
              value = this.toCurrency(data.reservation[k], true);
              break;
            case 'date':
              value = this.cleanDate(data.reservation[k], true);
              break;
            case 'string':
              value = String(data.reservation[k]);
              break;
          }
          this.stayAttributes.push({
            key: k,
            label: fieldLabels[k],
            value: value,
          });
        }
      }
      this.attrHeight = this.attributesLength * 36 + 180;
    },
  },
};
</script>
<style>
.incoming-call-frame {
  position: fixed;
  bottom: 10px;
  width: 300px;
  background: #fff;
  z-index: 60000;
  min-height: 320px;
  display: none;

  border: 2px #3333333a solid;
  border-radius: 2px;

  box-shadow: 5px 5px 0px 0px #00201d3a, 10px 10px 0px 0px #0037313a,
    15px 15px 0px 0px #0b50493a, 20px 20px 0px 0px #1f68603a,
    25px 25px 0px 0px #3b827a3a, 5px 5px 15px 5px rgba(0, 0, 0, 0);
}

/* Position classes */
.incoming-call-bottom-right {
  right: 10px;
}

.incoming-call-bottom-left {
  left: 10px;
}

.incoming-call-bottom-center {
  left: 50%;
  transform: translateX(-50%);
}

.incoming-call-attributes {
  background: #eee;
  border-radius: 2px;
  padding: 1.2em;
  margin-left: 1em;
  margin-right: 1em;
}
.incoming-call-hero-tags {
  display: flex;
  justify-content: center;
  padding: 0.4em 1em 0;
}
.queueName {
  text-align: center;
  padding-top: 0.2em;
  font-family: 'open sans';
  font-size: 18px;
}
.display-incoming-call {
  display: block;
}
.call-center-toggle-menu {
  width: 100%;
  height: 25px;
  text-align: center;
  padding-top: 0.2em;
  font-family: 'open sans';
  font-size: 18px;
  font-weight: 500;
}
.call-toggle-button {
  cursor: pointer;
}
</style>
